import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ImportItem } from '@/lib/imports';
import { aed, dayMonth } from '@/lib/format';

interface Props {
  item: ImportItem;
  versionId: string;
  onClose: () => void;
  onPatch: (body: Record<string, unknown>) => void;
}

/** Карточка распознанной позиции (29.10.2026): всё, что модель и проверки знают о строке — картинка из PDF,
 * цены, остатки, опции, характеристики, флаги, второй проход, сравнение с каталогом. */
export default function ItemDrawer({ item, versionId, onClose, onPatch }: Props) {
  const { t } = useTranslation();
  const flagLabel = (f: string) => t(`impX.flag.${f}`, { defaultValue: f });
  const row = (label: string, value: React.ReactNode) => (
    <div className="flex items-start justify-between gap-3 py-1.5 text-[12px]">
      <dt className="shrink-0 text-foreground-500">{label}</dt>
      <dd className="min-w-0 text-right font-medium text-foreground-900">{value ?? '—'}</dd>
    </div>
  );
  const num = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('en-AE'));
  const sp = item.secondPass;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-foreground-950/30" onClick={onClose}>
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-background-50 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-background-200 bg-background-50 px-5 py-3">
          <div className="min-w-0">
            <p className="tnum text-[10.5px] font-semibold uppercase tracking-[0.14em] text-foreground-500">
              {item.supplierArticle} {item.page ? `· p.${item.page}` : ''} {item.sku ? `· SKU ${item.sku}` : ''}
            </p>
            <h2 className="truncate text-[15px] font-semibold text-foreground-950">{item.name || item.supplierArticle}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label={t('common.close')} className="cursor-pointer text-foreground-400 hover:text-foreground-800">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-[220px_minmax(0,1fr)]">
          <div>
            <div className="flex aspect-square items-center justify-center overflow-hidden rounded-md border border-background-200 bg-white">
              {item.imageUrl ? <img src={item.imageUrl} alt="" className="max-h-[90%] max-w-[90%] object-contain" /> : <span className="px-3 text-center text-[11px] text-foreground-400">{t('imd.noImage')}</span>}
            </div>
            <p className="mt-1.5 text-[10.5px] leading-4 text-foreground-400">{item.imageUrl ? t('imd.imageFromPdf') : t('imd.imageHint')}</p>
            <div className="mt-3 flex flex-col gap-1.5">
              <button type="button" onClick={() => onPatch({ reviewed: !item.reviewed })} className={`inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 rounded-md border px-3 text-[12px] font-medium ${item.reviewed ? 'border-primary-300 bg-primary-50 text-primary-800' : 'border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100'}`}>
                <i className={`${item.reviewed ? 'ri-checkbox-circle-fill' : 'ri-checkbox-circle-line'} text-[14px]`} /> {item.reviewed ? t('imd.reviewedYes') : t('imd.markReviewed')}
              </button>
              <button type="button" onClick={() => onPatch({ excluded: !item.excluded })} className="inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100">
                <i className={`${item.excluded ? 'ri-add-circle-line' : 'ri-forbid-line'} text-[14px]`} /> {item.excluded ? t('imd.include') : t('imd.exclude')}
              </button>
              {item.sku && (
                <Link to={`/catalog/${encodeURIComponent(item.sku)}`} className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100">
                  <i className="ri-price-tag-3-line text-[14px]" /> {t('imd.openSku')}
                </Link>
              )}
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap gap-1.5">
              <span className={`rounded border px-1.5 py-0.5 text-[10.5px] font-medium ${item.confidence === 'high' ? 'border-primary-200 bg-primary-50 text-primary-800' : item.confidence === 'check' ? 'border-secondary-200 bg-secondary-50 text-secondary-800' : 'border-accent-300 bg-accent-100 text-accent-900'}`}>
                {t(`impReview.${item.confidence}`)} {Math.round(item.confidenceScore * 100)}
              </span>
              {item.reviewRequired && <span className="rounded border border-accent-300 bg-accent-50 px-1.5 py-0.5 text-[10.5px] font-medium text-accent-900">{t('impX.required')}</span>}
              {item.edited && <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-[10.5px] font-medium text-secondary-800">{t('impX.edited')}</span>}
              {item.specialOffer && <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[10.5px] font-medium text-accent-800">{t('impX.special')}</span>}
              {item.discontinued && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10.5px] font-medium text-rose-700">{t('impX.discontinued')}</span>}
              {item.excluded && <span className="rounded bg-background-200 px-1.5 py-0.5 text-[10.5px] font-medium text-foreground-600">{t('imd.excluded')}</span>}
              {item.flags.map((f) => (
                <span key={f} className={`rounded border px-1.5 py-0.5 text-[10.5px] font-medium ${f === 'no_cbm' || f === 'new_item' ? 'border-background-200 bg-background-100 text-foreground-600' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>{flagLabel(f)}</span>
              ))}
            </div>

            {item.reviewRequired && !item.reviewed && (
              <div className="mt-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-[12px] leading-5 text-accent-900">
                <p className="font-semibold">{t('imd.whyTitle')}</p>
                <ul className="list-disc pl-4">
                  {item.reviewReasons.map((r) => (
                    <li key={r}>{t(`imd.why.${r}`, { risk: Math.round(item.riskAed).toLocaleString('en-AE'), threshold: item.reviewThreshold.toLocaleString('en-AE'), stock: item.stock ?? 0, price: item.price ?? 0, flags: item.flags.filter((f) => f !== 'no_cbm' && f !== 'new_item').map(flagLabel).join(', '), conf: Math.round(item.confidenceScore * 100) })}</li>
                  ))}
                </ul>
                <p className="mt-1 text-[11.5px] text-accent-800/90">{t('imd.whyAction')}</p>
              </div>
            )}
            <h3 className="mt-4 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground-500">{t('imd.secPrices')}</h3>
            <dl className="divide-y divide-background-200/70">
              {row(t('impReview.colPartnerPrice'), item.price == null ? <span className="text-rose-700">{t('impX.setPrice')}</span> : aed(item.price, 2))}
              {row(t('impReview.colOldPrice'), item.oldStockPrice == null ? '—' : aed(item.oldStockPrice, 2))}
              {item.oldPrice != null && row(t('imd.catalogPrice'), <span className={item.price != null && item.oldPrice !== item.price ? (item.price > item.oldPrice ? 'text-rose-600' : 'text-primary-700') : ''}>{aed(item.oldPrice, 2)}</span>)}
              {row(t('impX.colRisk'), num(Math.round(item.riskAed)))}
              {item.packingVariants.length > 0 && row(t('imd.packingVariants'), item.packingVariants.map((v) => `${v.packing}: ${aed(v.price, 2)}`).join(' · '))}
              {item.options.length > 0 && row(t('impReview.colOptions'), item.options.map((o) => `${o.label}${o.surcharge ? ` +${o.surcharge}` : ''}`).join(' · '))}
            </dl>

            <h3 className="mt-4 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground-500">{t('imd.secStock')}</h3>
            <dl className="divide-y divide-background-200/70">
              {row(t('impReview.colStockNow'), num(item.stock))}
              {item.oldStock != null && row(t('imd.catalogStock'), num(item.oldStock))}
              {row(t('impReview.colIncoming'), item.incoming > 0 ? `+${item.incoming} ${item.eta ? dayMonth(item.eta) : item.etaText ?? ''}` : '—')}
              {row(t('impReview.colStockGroup'), item.stockGroup)}
              {row(t('imd.packing'), item.packing)}
              {row('CBM', item.cbm)}
            </dl>

            <h3 className="mt-4 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground-500">{t('imd.secProduct')}</h3>
            <dl className="divide-y divide-background-200/70">
              {row(t('impReview.colCategory'), item.category)}
              {row(t('impReview.colColour'), item.color)}
            </dl>
            {item.specs.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[12px] leading-5 text-foreground-700">
                {item.specs.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            )}

            {sp && (
              <>
                <h3 className="mt-4 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground-500">{t('imd.secSecondPass')}</h3>
                <dl className="divide-y divide-background-200/70">
                  {row(t('imd.spPrices'), (sp.prices ?? []).map((p) => aed(p, 2)).join(', ') || '—')}
                  {row(t('impReview.colStockNow'), num(sp.stock_now))}
                  {row(t('impReview.colIncoming'), num(sp.incoming_qty))}
                </dl>
              </>
            )}
            <p className="mt-4 text-[10.5px] text-foreground-400">{t('imd.versionHint', { id: versionId.slice(0, 8) })}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
